import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format, startOfWeek, addDays, isSameDay } from "date-fns";
import { Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useAppointments } from "@/hooks/useAppointments";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";

interface DashboardCalendarProps {
  compact?: boolean;
  selectedView?: "day" | "week" | "month";
}

export function DashboardCalendar({ compact = false, selectedView: externalView }: DashboardCalendarProps) {
  const [internalView, setInternalView] = useState<"day" | "week" | "month">("day");
  
  // Use external view if provided, otherwise use internal state
  const selectedView = externalView || internalView;
  
  // Update internal state when external view changes
  useEffect(() => {
    if (externalView) {
      setInternalView(externalView);
    }
  }, [externalView]);
  const { appointments } = useAppointments();
  const { profile } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get current week/month dates
  const startOfCurrentWeek = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday start
  
  // Determine how many days to show based on view
  let daysToShow = 1; // default for day view
  if (selectedView === "week") {
    daysToShow = 5; // weekdays
  } else if (selectedView === "month") {
    daysToShow = 35; // 5 weeks grid for month view
  }
  
  const currentDates = Array.from(
    { length: daysToShow },
    (_, i) => addDays(selectedView === "day" ? currentDate : startOfCurrentWeek, i)
  );

  // Limit time slots for compact view
  const timeSlots = compact
    ? ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"]
    : [
        "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
        "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
        "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM"
      ];

  // Filter appointments for the current view
  const filteredAppointments = appointments
    .filter(apt => {
      const aptDate = new Date(apt.date);
      return currentDates.some(date => isSameDay(aptDate, date));
    })
    .map(apt => {
      const aptDate = new Date(apt.date);
      const dayIndex = currentDates.findIndex(date => isSameDay(aptDate, date));
      return {
        ...apt,
        day: dayIndex + 1, // Convert to 1-based index for compatibility
      };
    });

  const getAppointmentStyle = (appointment: any) => {
    const colors = {
      "Consultation": "bg-blue-100 border-blue-300 text-blue-800",
      "Follow-up": "bg-green-100 border-green-300 text-green-800",
      "Procedure": "bg-purple-100 border-purple-300 text-purple-800",
      "Check-up": "bg-orange-100 border-orange-300 text-orange-800",
    };
    return colors[appointment.type as keyof typeof colors] || "bg-gray-100 border-gray-300 text-gray-800";
  };

  const navigatePrevious = () => {
    if (selectedView === "day") {
      setCurrentDate(prev => addDays(prev, -1));
    } else if (selectedView === "week") {
      setCurrentDate(prev => addDays(prev, -7));
    } else if (selectedView === "month") {
      setCurrentDate(prev => addDays(prev, -28));
    }
  };

  const navigateNext = () => {
    if (selectedView === "day") {
      setCurrentDate(prev => addDays(prev, 1));
    } else if (selectedView === "week") {
      setCurrentDate(prev => addDays(prev, 7));
    } else if (selectedView === "month") {
      setCurrentDate(prev => addDays(prev, 28));
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            {selectedView === "day" ? "Today's Schedule" : 
             selectedView === "week" ? "Weekly Schedule" : "Monthly Schedule"}
          </CardTitle>
          <CardDescription>
            {selectedView === "day" 
              ? format(currentDate, "EEEE, MMMM d, yyyy")
              : selectedView === "week" 
                ? `Week of ${format(startOfCurrentWeek, "MMMM d")}`
                : `Month of ${format(currentDate, "MMMM yyyy")}`}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            <Button variant="ghost" size="icon" onClick={navigatePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={navigateNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {!compact && (
            <div className="flex items-center border rounded-md overflow-hidden">
              <Button
                variant={selectedView === "day" ? "default" : "ghost"}
                size="sm"
                className="rounded-none"
                onClick={() => setInternalView("day")}
              >
                Day
              </Button>
              <Button
                variant={selectedView === "week" ? "default" : "ghost"}
                size="sm"
                className="rounded-none"
                onClick={() => setInternalView("week")}
              >
                Week
              </Button>
              <Button
                variant={selectedView === "month" ? "default" : "ghost"}
                size="sm"
                className="rounded-none"
                onClick={() => setInternalView("month")}
              >
                Month
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className={compact ? "p-2" : "p-4"}>
        {selectedView !== "month" ? (
          <>
            <div className="grid grid-cols-6 gap-2 mb-2">
              <div className="text-xs font-medium text-gray-500">Time</div>
              {currentDates.slice(0, selectedView === "day" ? 1 : 5).map((date, index) => (
                <div key={index} className="text-xs font-medium text-center text-gray-700 p-1">
                  {format(date, "EEE")}
                  <div className="text-xs text-gray-500">{format(date, "MMM d")}</div>
                </div>
              ))}
            </div>

            <div className={`grid grid-cols-6 gap-2 ${compact ? "max-h-[300px]" : "max-h-[500px]"} overflow-y-auto`}>
              {timeSlots.map((time) => (
                <div key={time} className="contents">
                  <div className="text-xs text-gray-500 py-1 border-t border-gray-100">
                    {time}
                  </div>
                  {currentDates.slice(0, selectedView === "day" ? 1 : 5).map((_, dayIndex) => {
                    const dayAppointments = filteredAppointments.filter(
                      (apt) => apt.day === dayIndex + 1 && apt.time === time
                    );
                    
                    return (
                      <div
                        key={`${time}-${dayIndex}`}
                        className={`${compact ? "min-h-[40px]" : "min-h-[60px]"} p-1 border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors`}
                      >
                        {dayAppointments.map((appointment) => (
                          <div
                            key={appointment.id}
                            className={`p-1 rounded-lg border-l-4 transition-shadow hover:shadow-md ${getAppointmentStyle(appointment)}`}
                          >
                            <div className="text-xs font-medium truncate">{appointment.patient}</div>
                            <div className="text-xs text-gray-600 truncate">
                              {appointment.reason || appointment.type}
                            </div>
                            {!compact && (
                              <>
                                <div className="text-xs text-gray-600 truncate">{appointment.provider}</div>
                                <div className="flex items-center gap-1 text-xs">
                                  <Clock className="h-3 w-3" />
                                  <span>{appointment.duration}min</span>
                                </div>
                              </>
                            )}
                            <div className="flex items-center justify-between mt-1">
                              <Badge variant="secondary" className="text-xs">
                                {appointment.duration}min
                              </Badge>
                              {appointment.status && (
                                <Badge 
                                  variant={appointment.status === "confirmed" ? "default" : 
                                         appointment.status === "cancelled" ? "destructive" : "outline"}
                                  className={`text-xs ${appointment.status === "confirmed" ? "bg-green-100 text-green-800 hover:bg-green-200" : ""}`}
                                >
                                  {appointment.status}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        ) : (
          // Month view - calendar grid
          <div className="mt-2">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <div key={day} className="text-xs font-medium text-center text-gray-700 p-1">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid - 5 weeks, 7 days each */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 35 }, (_, i) => {
                const date = addDays(startOfCurrentWeek, i);
                const dayAppointments = filteredAppointments.filter(
                  (apt) => apt.day === i + 1
                );
                
                return (
                  <div
                    key={i}
                    className={`min-h-[80px] p-2 border border-gray-100 rounded hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSameDay(date, new Date()) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="text-xs font-medium mb-1">{format(date, 'd')}</div>
                    
                    {/* Show up to 3 appointments with count of additional ones */}
                    {dayAppointments.slice(0, 3).map((appointment) => (
                      <div 
                        key={appointment.id}
                        className={`p-1 mb-1 rounded text-xs truncate border-l-2 ${getAppointmentStyle(appointment)}`}
                      >
                        {appointment.time} - {appointment.patient}
                      </div>
                    ))}
                    
                    {dayAppointments.length > 3 && (
                      <div className="text-xs text-blue-600 font-medium mt-1">
                        +{dayAppointments.length - 3} more
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        <div className="mt-4 flex justify-end">
          <Link to="/calendar">
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              View Full Calendar
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
